import { useState, useEffect } from 'react';
import { AlertTriangle, TrendingUp, TrendingDown, Flame, Snowflake, Shuffle } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const regimeConfig = {
  CRISIS: { icon: AlertTriangle, color: 'text-regime-crisis', bg: 'bg-regime-crisis/10', border: 'border-regime-crisis/30' },
  'RISK-ON': { icon: TrendingUp, color: 'text-regime-risk-on', bg: 'bg-regime-risk-on/10', border: 'border-regime-risk-on/30' },
  'RISK-OFF': { icon: TrendingDown, color: 'text-regime-risk-off', bg: 'bg-regime-risk-off/10', border: 'border-regime-risk-off/30' },
  INFLATIONARY: { icon: Flame, color: 'text-regime-inflationary', bg: 'bg-regime-inflationary/10', border: 'border-regime-inflationary/30' },
  DEFLATIONARY: { icon: Snowflake, color: 'text-regime-deflationary', bg: 'bg-regime-deflationary/10', border: 'border-regime-deflationary/30' },
  TRANSITIONAL: { icon: Shuffle, color: 'text-regime-transitional', bg: 'bg-regime-transitional/10', border: 'border-regime-transitional/30' },
};

const allocationLabels = {
  equities: 'Equities',
  commodities: 'Commodities',
  crypto: 'Crypto',
  cash: 'Cash',
  fixed_income: 'Fixed Income',
};

export default function RegimeIndicator() {
  const [regime, setRegime] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRegime() {
      try {
        const res = await fetch('/api/regime/current');
        if (res.ok) {
          const data = await res.json();
          setRegime(data);
        }
      } catch {
        // Backend not running
      } finally {
        setLoading(false);
      }
    }
    fetchRegime();
    const interval = setInterval(fetchRegime, 120000);
    return () => clearInterval(interval);
  }, []);

  const currentRegime = regime?.regime || 'TRANSITIONAL';
  const config = regimeConfig[currentRegime];
  const Icon = config.icon;
  const allocation = regime?.target_allocation ? JSON.parse(regime.target_allocation) : null;

  return (
    <ModuleContainer title="Regime Classifier" subtitle="Rule-based market regime detection with target allocations">
      <div className={`rounded-lg p-4 ${config.bg} border ${config.border}`}>
        <div className="flex items-center gap-3 mb-3">
          <Icon className={`w-8 h-8 ${config.color}`} />
          <div>
            <h3 className={`text-xl font-mono font-bold ${config.color}`}>{currentRegime}</h3>
            <p className="text-xs text-korinth-muted">
              {regime?.confidence ? `${regime.confidence}% confidence` : 'Awaiting data...'}
              {regime?.started_at && ` — since ${new Date(regime.started_at).toLocaleDateString()}`}
            </p>
          </div>
        </div>

        {allocation && (
          <div>
            <p className="text-xs font-mono text-korinth-muted mb-2">TARGET ALLOCATION</p>
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(allocation).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="text-lg font-mono font-bold text-white">{value}%</div>
                  <div className="text-[10px] text-korinth-muted">{allocationLabels[key] || key}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!regime && !loading && (
          <p className="text-xs text-korinth-muted font-mono">
            Regime classifier needs price data. Start the backend and wait for the first data fetch cycle.
          </p>
        )}
      </div>
    </ModuleContainer>
  );
}
